import React, { useEffect, useState, useCallback, useMemo } from "react";
import { PaginationBar, paginate } from "@/components/Pagination";
import api, { apiError } from "@/lib/api";
import IndustrySelect, { useIndustries } from "@/components/IndustrySelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Plus, Pencil, Trash2, Loader2, Search, Star, ArrowRight, FileUp, LayoutGrid, Table2, Layers, X, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { value: "not_contacted", label: "Not Contacted", dot: "bg-slate-400", cls: "bg-slate-100 text-slate-600" },
  { value: "in_discussion", label: "In Discussion", dot: "bg-[#1B4383]", cls: "bg-[#E6ECF5] text-[#1B4383]" },
  { value: "converted", label: "Converted", dot: "bg-[#527D63]", cls: "bg-[#DCEBE1] text-[#2F6B45]" },
  { value: "dropped", label: "Dropped", dot: "bg-[#9D4848]", cls: "bg-[#F4E7E7] text-[#9D4848]" },
];
const stMeta = (v) => STATUSES.find((s) => s.value === v) || STATUSES[0];
const EMPTY = { company: "", industry: "", region: "", oem_brands: [], key_oem_brands: [], contact_name: "", contact_role: "", contact_phone: "", contact_email: "", status: "not_contacted", notes: "", assigned_to_id: "" };

function readFileBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
const docTypeOf = (f) => {
  const n = (f.name || "").toLowerCase();
  if (n.endsWith(".xlsx") || n.endsWith(".xls") || n.endsWith(".csv")) return "excel";
  if (n.endsWith(".pdf")) return "pdf";
  if (f.type?.startsWith("image/")) return "image";
  return "";
};

function BrandChips({ brands, keyBrands }) {
  if (!brands?.length) return <span className="text-slate-300">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {brands.map((b) => {
        const isKey = keyBrands?.includes(b);
        return <span key={b} className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium ${isKey ? "bg-[#F6EDD9] text-[#A87F31]" : "bg-[#EEF1F6] text-[#5B6A7D]"}`}>{isKey && <Star className="h-2.5 w-2.5 fill-[#A87F31]" />}{b}</span>;
      })}
    </div>
  );
}

function OemSelect({ options, value, onChange, testid, onCreatePartner }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const all = Array.from(new Set([...(options || []), ...(value || [])])).sort();
  const filtered = all.filter((b) => b.toLowerCase().includes(q.toLowerCase()));
  const toggle = (b) => onChange(value.includes(b) ? value.filter((x) => x !== b) : [...value, b]);
  const doAdd = async () => {
    const n = newName.trim();
    if (!n || !onCreatePartner) return;
    setAdding(true);
    const created = await onCreatePartner(n);
    setAdding(false);
    if (created) { if (!value.includes(created)) onChange([...value, created]); setNewName(""); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-border bg-white px-2 py-1.5 text-left text-sm hover:border-slate-400" data-testid={testid}>
          {value.length ? value.map((b) => <span key={b} className="rounded bg-[#E6ECF5] px-1.5 py-0.5 text-xs text-[#1B4383]">{b}</span>) : <span className="text-muted-foreground">Select OEM Partners…</span>}
          <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <div className="border-b border-border p-2"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search OEM partners…" className="h-8 text-xs" data-testid={`${testid}-search`} /></div>
        <div className="max-h-56 overflow-y-auto p-1" data-testid={`${testid}-list`}>
          {filtered.map((b) => {
            const on = value.includes(b);
            return (
              <button key={b} type="button" onClick={() => toggle(b)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-slate-50" data-testid={`${testid}-opt-${b}`}>
                <span className={`flex h-4 w-4 items-center justify-center rounded border ${on ? "border-[#1B4383] bg-[#1B4383] text-white" : "border-slate-300"}`}>{on && <Check className="h-3 w-3" />}</span>
                <span className="flex-1 truncate">{b}</span>
              </button>
            );
          })}
          {!filtered.length && <div className="px-2 py-3 text-center text-xs text-muted-foreground">No matching OEM partner.</div>}
        </div>
        {onCreatePartner && (
          <div className="flex items-center gap-1 border-t border-border p-2" data-testid={`${testid}-add-row`}>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doAdd(); } }} placeholder="New OEM partner…" className="h-8 text-xs" data-testid={`${testid}-new`} />
            <Button type="button" size="sm" variant="outline" className="h-8 shrink-0 px-2" onClick={doAdd} disabled={adding || !newName.trim()} title="Create OEM partner" data-testid={`${testid}-add`}>{adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}</Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}


const REGION_COORDS = {
  "dar es salaam": { lat: -6.79, lng: 39.21 }, "dar": { lat: -6.79, lng: 39.21 },
  "dodoma": { lat: -6.17, lng: 35.74 }, "arusha": { lat: -3.37, lng: 36.68 },
  "mwanza": { lat: -2.52, lng: 32.90 }, "mbeya": { lat: -8.90, lng: 33.46 },
  "morogoro": { lat: -6.82, lng: 37.66 }, "tanga": { lat: -5.07, lng: 39.10 },
  "kilimanjaro": { lat: -3.35, lng: 37.34 }, "moshi": { lat: -3.35, lng: 37.34 },
  "tabora": { lat: -5.02, lng: 32.83 }, "kigoma": { lat: -4.88, lng: 29.63 },
  "iringa": { lat: -7.77, lng: 35.70 }, "mtwara": { lat: -10.27, lng: 40.18 },
  "songea": { lat: -10.68, lng: 35.65 }, "ruvuma": { lat: -10.68, lng: 35.65 },
  "musoma": { lat: -1.50, lng: 33.80 }, "mara": { lat: -1.50, lng: 33.80 },
  "shinyanga": { lat: -3.66, lng: 33.42 }, "singida": { lat: -4.82, lng: 34.75 },
  "sumbawanga": { lat: -7.97, lng: 31.62 }, "rukwa": { lat: -7.97, lng: 31.62 },
  "bukoba": { lat: -1.33, lng: 31.81 }, "kagera": { lat: -1.33, lng: 31.81 },
  "zanzibar": { lat: -6.16, lng: 39.20 }, "pwani": { lat: -7.00, lng: 38.90 },
  "coast": { lat: -7.00, lng: 38.90 }, "njombe": { lat: -9.34, lng: 34.77 },
  "lindi": { lat: -9.99, lng: 39.72 }, "manyara": { lat: -4.21, lng: 35.75 },
  "babati": { lat: -4.21, lng: 35.75 }, "katavi": { lat: -6.50, lng: 31.00 },
  "simiyu": { lat: -2.83, lng: 34.00 }, "geita": { lat: -2.87, lng: 32.23 },
};
const regionCoords = (region) => {
  const q = (region || "").trim().toLowerCase();
  if (!q) return null;
  if (REGION_COORDS[q]) return REGION_COORDS[q];
  const k = Object.keys(REGION_COORDS).find((key) => q.includes(key) || key.includes(q));
  return k ? REGION_COORDS[k] : null;
};
const TZ_BORDER = [
  [30.4, -1.0], [31.9, -1.0], [34.0, -1.05], [37.6, -3.05], [39.2, -4.7], [39.5, -6.5],
  [39.8, -8.0], [40.4, -10.3], [39.0, -10.9], [37.9, -11.3], [35.6, -11.6], [34.6, -11.5],
  [34.3, -9.7], [33.9, -9.5], [32.9, -9.4], [31.9, -9.0], [31.0, -8.6], [30.2, -7.0],
  [29.4, -6.0], [29.35, -4.5], [29.9, -3.3], [30.5, -2.4],
];

function TanzaniaMap({ points }) {
  const W = 320, H = 340;
  const proj = ([lng, lat]) => `${(((lng - 29.2) / 11.4) * W).toFixed(1)},${(((-0.8 - lat) / 11.1) * H).toFixed(1)}`;
  const poly = TZ_BORDER.map(proj).join(" ");
  return (
    <div className="rounded-lg border border-border bg-gradient-to-b from-[#0B1E3B] to-[#12294B] p-4" data-testid="tc-map">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-heading text-sm font-bold text-white">Client Map · Tanzania</h3>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => <span key={s.value} className="flex items-center gap-1 text-[10px] font-medium text-slate-300"><span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />{s.label}</span>)}
        </div>
      </div>
      <div className="relative mx-auto w-full" style={{ maxWidth: 420, aspectRatio: "320 / 340" }}>
        <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 h-full w-full" preserveAspectRatio="xMidYMid meet">
          <polygon points={poly} fill="#1B3358" stroke="#3E5C88" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
        {points.pts.map((p) => (
          <div key={p.id} className="group/dot absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${p.left}%`, top: `${p.top}%` }} data-testid={`tc-map-dot-${p.id}`}>
            <span className={`absolute left-1/2 top-1/2 inline-flex h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full opacity-60 ${stMeta(p.status).dot}`} />
            <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ring-2 ring-white/70 ${stMeta(p.status).dot}`} />
            <span className="pointer-events-none absolute left-1/2 top-4 z-10 -translate-x-1/2 whitespace-nowrap rounded bg-black/80 px-1.5 py-0.5 text-[10px] text-white opacity-0 transition-opacity group-hover/dot:opacity-100">{p.company} · {p.region}</span>
          </div>
        ))}
        {!points.pts.length && <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400">Add a region to a target client to see it here.</div>}
      </div>
      {points.unmapped > 0 && <p className="mt-2 text-center text-[10px] text-slate-400">{points.unmapped} client(s) without a recognised region aren't shown.</p>}
    </div>
  );
}


export default function TargetClients() {
  const [rows, setRows] = useState([]);
  const [brands, setBrands] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ search: "", status: "all", oem: "all" });
  const [dlg, setDlg] = useState(null); // {mode, id, ...form}
  const [saving, setSaving] = useState(false);
  const [imp, setImp] = useState(null); // { files, parsed, busy }
  const [impSel, setImpSel] = useState(new Set());
  const [bulkBrands, setBulkBrands] = useState([]);
  const [team, setTeam] = useState([]);
  useEffect(() => { api.get("/team-members").then((r) => setTeam(r.data)).catch(() => {}); }, []);
  const { industries } = useIndustries();
  const indLabel = (v) => industries.find((x) => x.value === v)?.label || v;

  const load = useCallback(() => {
    setLoading(true);
    api.get("/target-clients").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
    api.get("/target-clients/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); api.get("/target-clients/oem-brands").then((r) => setBrands(r.data)).catch(() => {}); }, [load]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const shown = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filters.status !== "all" && r.status !== filters.status) return false;
      if (filters.oem !== "all" && !(r.oem_brands || []).includes(filters.oem)) return false;
      if (q) {
        const hay = `${r.company} ${r.region} ${r.industry} ${(r.oem_brands || []).join(" ")} ${r.contact_name} ${r.notes}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, filters]);
  const paged = paginate(shown, page, 50);
  useEffect(() => { setPage(1); }, [filters, view]);
  const mapPoints = useMemo(() => {
    const pts = []; let unmapped = 0; const seen = {};
    shown.forEach((r) => {
      const c = regionCoords(r.region);
      if (!c) { unmapped += 1; return; }
      const key = `${c.lat},${c.lng}`; const n = (seen[key] = (seen[key] || 0) + 1); const j = (n - 1) * 0.9;
      pts.push({ id: r.id, company: r.company, region: r.region, status: r.status, left: ((c.lng - 29.2) / 11.4) * 100 + j, top: ((-0.8 - c.lat) / 11.1) * 100 + j });
    });
    return { pts, unmapped };
  }, [shown]);

  const openNew = () => { setDlg({ mode: "add", ...EMPTY }); };
  const openEdit = (r) => { setDlg({ mode: "edit", id: r.id, company: r.company, industry: r.industry, region: r.region, oem_brands: [...(r.oem_brands || [])], key_oem_brands: [...(r.key_oem_brands || [])], contact_name: r.contact_name, contact_role: r.contact_role, contact_phone: r.contact_phone, contact_email: r.contact_email, status: r.status, notes: r.notes, assigned_to_id: r.assigned_to_id || "" }); };

  const toggleKey = (b) => setDlg((d) => {
    if (!d.oem_brands.includes(b)) return { ...d, oem_brands: [...d.oem_brands, b], key_oem_brands: [...d.key_oem_brands, b] };
    return { ...d, key_oem_brands: d.key_oem_brands.includes(b) ? d.key_oem_brands.filter((x) => x !== b) : [...d.key_oem_brands, b] };
  });
  const createPartner = async (name) => {
    const n = (name || "").trim();
    if (!n) return null;
    const dupe = brands.find((b) => b.toLowerCase() === n.toLowerCase());
    if (dupe) { toast.info("That OEM partner already exists."); return dupe; }
    try {
      await api.post("/suppliers", { name: n, partner_type: "oem" });
      const { data } = await api.get("/target-clients/oem-brands");
      setBrands(data);
      toast.success(`OEM partner "${n}" added`);
      return n;
    } catch (e) { toast.error(apiError(e)); return null; }
  };

  const save = async () => {
    if (!dlg.company.trim()) { toast.error("Company name is required."); return; }
    setSaving(true);
    const body = { company: dlg.company.trim(), industry: dlg.industry || "", region: dlg.region || "", oem_brands: dlg.oem_brands, key_oem_brands: dlg.key_oem_brands, contact_name: dlg.contact_name || "", contact_role: dlg.contact_role || "", contact_phone: dlg.contact_phone || "", contact_email: dlg.contact_email || "", status: dlg.status, notes: dlg.notes || "", assigned_to_id: dlg.assigned_to_id || "", assigned_to_name: (team.find((t) => t.id === dlg.assigned_to_id) || {}).name || "" };
    try {
      if (dlg.mode === "edit") await api.put(`/target-clients/${dlg.id}`, body);
      else await api.post("/target-clients", body);
      toast.success(dlg.mode === "edit" ? "Target client updated" : "Target client added");
      setDlg(null); load(); api.get("/target-clients/oem-brands").then((r) => setBrands(r.data)).catch(() => {});
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const move = async (r, status) => {
    setRows((p) => p.map((x) => (x.id === r.id ? { ...x, status } : x)));
    try { await api.put(`/target-clients/${r.id}/move`, { status }); api.get("/target-clients/summary").then((res) => setSummary(res.data)); }
    catch (e) { toast.error(apiError(e)); load(); }
  };
  const remove = async (r) => { if (!window.confirm(`Delete ${r.company}?`)) return; try { await api.delete(`/target-clients/${r.id}`); setRows((p) => p.filter((x) => x.id !== r.id)); toast.success("Deleted"); } catch (e) { toast.error(apiError(e)); } };
  const convert = async (r) => {
    if (!window.confirm(`Convert ${r.company} into a CRM client and a new Opportunity?`)) return;
    try { const { data } = await api.post(`/target-clients/${r.id}/convert`, {}); toast.success(`Converted → ${data.opportunity_code}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // ---- Import ----
  const onImportFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => docTypeOf(f));
    if (!files.length) { toast.error("Add an Excel/CSV, PDF or image file."); return; }
    setImp((s) => ({ ...(s || {}), files, parsed: null, busy: true }));
    try {
      const documents = [];
      for (const f of files) documents.push({ source_type: docTypeOf(f), file_base64: await readFileBase64(f), filename: f.name });
      const { data } = await api.post("/target-clients/import/parse", { documents });
      const byLabel = {};
      industries.forEach((x) => { byLabel[x.label.toLowerCase()] = x.value; byLabel[x.value.toLowerCase()] = x.value; });
      const rows = (data.rows || []).map((r) => ({ ...r, status: "not_contacted", industry: byLabel[(r.industry || "").toLowerCase()] || r.industry || "" }));
      setImp({ files, parsed: rows, busy: false });
      setImpSel(new Set()); setBulkBrands([]);
      if (!rows.length) toast.warning("No companies detected in that file.");
    } catch (e) { toast.error(apiError(e)); setImp((s) => ({ ...(s || {}), busy: false })); }
  };
  const doImport = async () => {
    const good = (imp.parsed || []).filter((r) => (r.company || "").trim());
    if (!good.length) { toast.error("Nothing to import."); return; }
    setImp((s) => ({ ...s, busy: true }));
    try { const { data } = await api.post("/target-clients/import", { rows: good }); toast.success(`Imported ${data.created} target client(s)`); setImp(null); load(); api.get("/target-clients/oem-brands").then((r) => setBrands(r.data)); }
    catch (e) { toast.error(apiError(e)); setImp((s) => ({ ...s, busy: false })); }
  };
  const editParsed = (i, k, v) => setImp((s) => ({ ...s, parsed: s.parsed.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));
  const dropParsed = (i) => { setImp((s) => ({ ...s, parsed: s.parsed.filter((_, j) => j !== i) })); setImpSel(new Set()); };
  const toggleImpRow = (i) => setImpSel((s) => { const n = new Set(s); if (n.has(i)) n.delete(i); else n.add(i); return n; });
  const toggleImpAll = () => setImpSel((s) => (s.size === (imp?.parsed?.length || 0) ? new Set() : new Set((imp?.parsed || []).map((_, i) => i))));
  const applyBulkBrands = (v) => { setBulkBrands(v); setImp((s) => ({ ...s, parsed: s.parsed.map((r, j) => (impSel.has(j) ? { ...r, oem_brands: v } : r)) })); };
  const applyBulkField = (field, v) => setImp((s) => ({ ...s, parsed: s.parsed.map((r, j) => (impSel.has(j) ? { ...r, [field]: v } : r)) }));
  const closeImport = () => { setImp(null); setImpSel(new Set()); setBulkBrands([]); };

  return (
    <div data-testid="target-clients">
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="tc-kpis">
        <div className="rounded-lg border border-border bg-white p-4" data-testid="tc-kpi-total">
          <div className="mb-2 flex items-center justify-between"><span className="text-[11px] font-semibold tracking-wide text-slate-400">Total</span><span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#EEF1F6] text-[#5B6A7D]"><Layers className="h-4 w-4" /></span></div>
          <div className="font-heading text-2xl font-bold text-slate-900">{summary?.total ?? 0}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">Target Clients</div>
        </div>
        {STATUSES.map((s) => (
          <div key={s.value} className="rounded-lg border border-border bg-white p-4" data-testid={`tc-kpi-${s.value}`}>
            <div className="mb-2 flex items-center justify-between"><span className="text-[11px] font-semibold tracking-wide text-slate-400">{s.label}</span><span className={`flex h-7 w-7 items-center justify-center rounded-md ${s.cls}`}><span className={`h-2 w-2 rounded-full ${s.dot}`} /></span></div>
            <div className="font-heading text-2xl font-bold text-slate-900">{summary?.counts?.[s.value] ?? 0}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">&nbsp;</div>
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-col gap-3 rounded-lg border border-border bg-white p-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search company, region, notes…" value={filters.search} onChange={(e) => setF("search", e.target.value)} data-testid="tc-search" />
        </div>
        <Select value={filters.status} onValueChange={(v) => setF("status", v)}>
          <SelectTrigger className="lg:w-44" data-testid="tc-filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.oem} onValueChange={(v) => setF("oem", v)}>
          <SelectTrigger className="lg:w-44" data-testid="tc-filter-oem"><SelectValue placeholder="All OEM Partners" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All OEM Partners</SelectItem>{brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImp({ files: [], parsed: null, busy: false })} data-testid="tc-import-btn"><FileUp className="mr-2 h-4 w-4" />Bulk Import</Button>
          <Button onClick={openNew} data-testid="tc-new-btn"><Plus className="mr-2 h-4 w-4" />New Target Client</Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex overflow-hidden rounded-md border border-border" data-testid="tc-view-toggle">
          {[["list", "List", Table2], ["board", "Board", LayoutGrid], ["coverage", "OEM Coverage", Layers]].map(([v, l, Ic]) => (
            <button key={v} onClick={() => setView(v)} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors ${view === v ? "bg-[#12294B] text-white" : "text-slate-500 hover:bg-slate-50"}`} data-testid={`tc-view-${v}`}><Ic className="h-3.5 w-3.5" />{l}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : view === "list" ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="tc-list-wrap">
        <div className="space-y-3 lg:col-span-2">
        <div className="overflow-x-auto rounded-lg border border-border bg-white" data-testid="tc-list">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border bg-slate-50 text-left text-[11px] tracking-wide text-slate-500 [&_th]:whitespace-nowrap">
              <th className="px-4 py-3 font-semibold">Company</th><th className="px-4 py-3 font-semibold hidden sm:table-cell">Industry</th><th className="px-4 py-3 font-semibold hidden md:table-cell">Region</th><th className="px-4 py-3 font-semibold hidden lg:table-cell">OEM Brands</th><th className="px-4 py-3 font-semibold hidden lg:table-cell">Primary Contact</th><th className="px-4 py-3 font-semibold hidden sm:table-cell">Status</th><th className="px-4 py-3 text-right font-semibold">Actions</th>
            </tr></thead>
            <tbody>
              {paged.map((r) => (
                <tr key={r.id} onClick={() => openEdit(r)} className="group cursor-pointer border-b border-border last:border-0 hover:bg-slate-50" data-testid={`tc-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{r.company}</div>
                    {r.notes && <div className="line-clamp-1 text-xs text-muted-foreground">{r.notes}</div>}
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 sm:hidden">
                      {r.industry && <span className="rounded bg-[#DCEBE1] px-1.5 py-0.5 text-[10px] font-medium text-[#2F6B45]">{indLabel(r.industry)}</span>}
                      {r.region && <span className="text-[11px] text-slate-500">{r.region}</span>}
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${stMeta(r.status).cls}`}><span className={`h-1.5 w-1.5 rounded-full ${stMeta(r.status).dot}`} />{stMeta(r.status).label}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">{r.industry ? <span className="rounded bg-[#DCEBE1] px-1.5 py-0.5 text-[10px] font-medium text-[#2F6B45]">{indLabel(r.industry)}</span> : "—"}</td>
                  <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{r.region || "—"}</td>
                  <td className="px-4 py-3 hidden lg:table-cell"><BrandChips brands={r.oem_brands} keyBrands={r.key_oem_brands} /></td>
                  <td className="px-4 py-3 hidden lg:table-cell">{r.contact_name ? <div><span className="font-medium text-slate-800">{r.contact_name}</span>{r.contact_role && <span className="text-xs text-muted-foreground"> · {r.contact_role}</span>}<div className="text-[11px] text-muted-foreground">{[r.contact_phone, r.contact_email].filter(Boolean).join(" · ")}</div></div> : "—"}</td>
                  <td className="px-4 py-3 hidden sm:table-cell"><span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${stMeta(r.status).cls}`}><span className={`h-1.5 w-1.5 rounded-full ${stMeta(r.status).dot}`} />{stMeta(r.status).label}</span></td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1.5">
                      {r.status !== "converted" && <button onClick={() => convert(r)} className="inline-flex items-center gap-1 text-xs font-medium text-[#527D63] hover:underline" data-testid={`tc-convert-${r.id}`}><ArrowRight className="h-3.5 w-3.5" />Convert</button>}
                      {r.converted_opportunity_code && <span className="rounded bg-[#DCEBE1] px-1.5 py-0.5 text-[10px] font-medium text-[#2F6B45]">{r.converted_opportunity_code}</span>}
                      <button onClick={() => openEdit(r)} className="text-slate-400 opacity-100 transition-opacity hover:text-primary sm:opacity-0 sm:group-hover:opacity-100" data-testid={`tc-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => remove(r)} className="text-slate-400 opacity-100 transition-opacity hover:text-destructive sm:opacity-0 sm:group-hover:opacity-100" data-testid={`tc-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!shown.length && <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">No target clients yet. Add one or bulk-import your prospect list.</td></tr>}
            </tbody>
          </table>
        </div>
        <PaginationBar page={page} setPage={setPage} total={shown.length} size={50} testid="tc-pagination" />
        </div>
        <TanzaniaMap points={mapPoints} />
        </div>
      ) : view === "board" ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="tc-board">
          {STATUSES.map((col) => {
            const list = shown.filter((r) => r.status === col.value);
            return (
              <div key={col.value} className="rounded-lg border border-border bg-slate-50/60" data-testid={`tc-col-${col.value}`}>
                <div className="flex items-center justify-between px-4 py-3"><h3 className="flex items-center gap-1.5 font-heading text-sm font-bold text-slate-800"><span className={`h-2 w-2 rounded-full ${col.dot}`} />{col.label}</h3><span className="rounded-full border border-border bg-white px-2 py-0.5 text-xs text-slate-500">{list.length}</span></div>
                <div className="space-y-2.5 px-3 pb-3">
                  {list.map((r) => (
                    <div key={r.id} onClick={() => openEdit(r)} className="group cursor-pointer rounded-md border border-border bg-white p-3 shadow-sm" data-testid={`tc-card-${r.id}`}>
                      <div className="flex items-start justify-between gap-2"><div className="font-medium text-slate-900">{r.company}</div>
                        <div className="flex gap-1 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100" onClick={(e) => e.stopPropagation()}><button onClick={() => openEdit(r)} className="text-slate-400 hover:text-primary" data-testid={`tc-card-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button><button onClick={() => remove(r)} className="text-slate-400 hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button></div>
                      </div>
                      {r.region && <div className="text-xs text-muted-foreground">{r.region}</div>}
                      <div className="mt-1.5"><BrandChips brands={r.oem_brands} keyBrands={r.key_oem_brands} /></div>
                      <div className="mt-2.5 flex items-center gap-1.5 border-t border-border pt-2" onClick={(e) => e.stopPropagation()}>
                        <Select value={r.status} onValueChange={(v) => move(r, v)}><SelectTrigger className="h-7 flex-1 text-xs" data-testid={`tc-move-${r.id}`}><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
                        {r.status !== "converted" && <button onClick={() => convert(r)} title="Convert" className="rounded-md border border-border p-1.5 text-[#527D63] hover:bg-[#DCEBE1]" data-testid={`tc-card-convert-${r.id}`}><ArrowRight className="h-3.5 w-3.5" /></button>}
                      </div>
                    </div>
                  ))}
                  {!list.length && <div className="py-6 text-center text-xs text-slate-400">Empty</div>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="tc-coverage">
          {(summary?.coverage || []).map((c) => {
            const list = rows.filter((r) => (r.oem_brands || []).includes(c.brand));
            return (
              <div key={c.brand} className="rounded-xl border border-border bg-white" data-testid={`tc-cov-${c.brand}`}>
                <div className="flex items-center justify-between border-b border-border px-4 py-3"><h3 className="font-heading text-sm font-bold text-slate-900">{c.brand}</h3><span className="rounded-full bg-[#EEF1F6] px-2 py-0.5 text-xs font-medium text-slate-600">{c.count}</span></div>
                <ul className="divide-y divide-border">
                  {list.map((r) => (
                    <li key={r.id} className="flex items-center justify-between px-4 py-2 text-sm"><span className="text-slate-700">{r.company}{r.key_oem_brands?.includes(c.brand) && <Star className="ml-1 inline h-3 w-3 fill-[#A87F31] text-[#A87F31]" />}</span><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${stMeta(r.status).cls}`}>{stMeta(r.status).label}</span></li>
                  ))}
                </ul>
              </div>
            );
          })}
          {!(summary?.coverage || []).length && <div className="col-span-full rounded-lg border border-dashed border-border py-12 text-center text-muted-foreground">No OEM brands mapped yet.</div>}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="tc-dialog">
          <DialogHeader><DialogTitle>{dlg?.mode === "edit" ? "Edit Target Client" : "New Target Client"}</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-4">
              <div className="space-y-2"><Label>Company</Label><Input value={dlg.company} onChange={(e) => setDlg({ ...dlg, company: e.target.value })} placeholder="e.g. Bonnite Bottlers" data-testid="tc-company" /></div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2"><Label>Industry</Label><IndustrySelect value={dlg.industry} onChange={(v) => setDlg({ ...dlg, industry: v })} testid="tc-industry" /></div>
                <div className="space-y-2"><Label>Region</Label><Input value={dlg.region} onChange={(e) => setDlg({ ...dlg, region: e.target.value })} placeholder="e.g. Dar es Salaam" data-testid="tc-region" /></div>
              </div>
              <div className="space-y-2">
                <Label>OEM Brands <span className="font-normal text-muted-foreground">(from Suppliers → OEM Partners · star = key)</span></Label>
                <OemSelect options={brands} value={dlg.oem_brands} onChange={(v) => setDlg((d) => ({ ...d, oem_brands: v, key_oem_brands: d.key_oem_brands.filter((k) => v.includes(k)) }))} testid="tc-brands" onCreatePartner={createPartner} />
                {dlg.oem_brands.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="tc-key-chips">
                    {dlg.oem_brands.map((b) => {
                      const isKey = dlg.key_oem_brands.includes(b);
                      return (
                        <button type="button" key={b} onClick={() => toggleKey(b)} title="Tap the star to mark this OEM as key" data-testid={`tc-brand-key-${b}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${isKey ? "border-[#A87F31] bg-[#F6EDD9] text-[#A87F31]" : "border-border bg-white text-slate-500"}`}>
                          <Star className={`h-3 w-3 ${isKey ? "fill-[#A87F31] text-[#A87F31]" : "text-slate-300"}`} />{b}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2"><Label>Contact Name</Label><Input value={dlg.contact_name} onChange={(e) => setDlg({ ...dlg, contact_name: e.target.value })} data-testid="tc-contact-name" /></div>
                <div className="space-y-2"><Label>Contact Role</Label><Input value={dlg.contact_role} onChange={(e) => setDlg({ ...dlg, contact_role: e.target.value })} placeholder="e.g. Procurement Manager" data-testid="tc-contact-role" /></div>
                <div className="space-y-2"><Label>Phone</Label><Input value={dlg.contact_phone} onChange={(e) => setDlg({ ...dlg, contact_phone: e.target.value })} data-testid="tc-contact-phone" /></div>
                <div className="space-y-2"><Label>Email</Label><Input value={dlg.contact_email} onChange={(e) => setDlg({ ...dlg, contact_email: e.target.value })} data-testid="tc-contact-email" /></div>
              </div>
              <div className="space-y-2"><Label>Status</Label>
                <Select value={dlg.status} onValueChange={(v) => setDlg({ ...dlg, status: v })}><SelectTrigger data-testid="tc-status"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-2"><Label>Assigned To</Label>
                <Select value={dlg.assigned_to_id || "unassigned"} onValueChange={(v) => setDlg({ ...dlg, assigned_to_id: v === "unassigned" ? "" : v })}><SelectTrigger data-testid="tc-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger><SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{team.map((t) => <SelectItem key={t.id} value={t.id}>{t.full_name || t.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-2"><Label>Notes</Label><Textarea rows={2} value={dlg.notes} onChange={(e) => setDlg({ ...dlg, notes: e.target.value })} data-testid="tc-notes" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="tc-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={!!imp} onOpenChange={(o) => !o && closeImport()}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="tc-import-dialog">
          <DialogHeader><DialogTitle>Bulk Import Target Clients</DialogTitle></DialogHeader>
          <DialogDescription className="-mt-2">Upload an Excel/CSV, PDF or image of your prospect list. AI reads it into rows — review, tweak, then import.</DialogDescription>
          <div className="space-y-4">
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border bg-slate-50 py-6 text-sm font-medium text-slate-600 hover:border-primary hover:text-primary" data-testid="tc-import-drop">
              {imp?.busy && !imp?.parsed ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} {imp?.files?.length ? imp.files.map((f) => f.name).join(", ") : "Choose Excel / CSV / PDF / image"}
              <input type="file" multiple accept=".xlsx,.xls,.csv,.pdf,image/*" className="hidden" onChange={(e) => onImportFiles(e.target.files)} data-testid="tc-import-input" />
            </label>
            {imp?.parsed && (
              <div className="rounded-md border border-border" data-testid="tc-import-review">
                <div className="flex flex-wrap items-center gap-3 border-b border-border bg-slate-50 px-3 py-2 text-xs">
                  <span className="font-semibold text-slate-600">{imp.parsed.length} companies detected — review before importing</span>
                  {impSel.size > 0 && (
                    <div className="flex flex-1 flex-wrap items-center gap-2" data-testid="tc-import-bulkbar">
                      <span className="font-semibold text-[#1B4383]">{impSel.size} selected · apply to all:</span>
                      <div className="w-48"><OemSelect options={brands} value={bulkBrands} onChange={applyBulkBrands} testid="tc-import-bulk-oem" onCreatePartner={createPartner} /></div>
                      <div className="w-40"><IndustrySelect value="" onChange={(v) => applyBulkField("industry", v)} className="h-8 text-xs" testid="tc-import-bulk-industry" /></div>
                      <Select value="" onValueChange={(v) => applyBulkField("status", v)}>
                        <SelectTrigger className="h-8 w-36 text-xs" data-testid="tc-import-bulk-status"><SelectValue placeholder="Set status" /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <button onClick={() => setImpSel(new Set())} className="text-slate-500 hover:underline" data-testid="tc-import-clear-sel">Clear</button>
                    </div>
                  )}
                </div>
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-slate-500">
                      <th className="w-8 px-2 py-1.5"><Checkbox checked={imp.parsed.length > 0 && impSel.size === imp.parsed.length} onCheckedChange={toggleImpAll} aria-label="Select all" data-testid="tc-import-select-all" /></th>
                      <th className="px-2 py-1.5">Company</th><th className="px-2 py-1.5">Industry</th><th className="px-2 py-1.5">Region</th><th className="px-2 py-1.5">OEM Brands</th><th className="px-2 py-1.5">Status</th><th className="px-2 py-1.5" />
                    </tr></thead>
                    <tbody>
                      {imp.parsed.map((r, i) => (
                        <tr key={i} className={`border-t border-border ${impSel.has(i) ? "bg-[#EEF4FF]" : ""}`} data-testid={`tc-import-row-${i}`}>
                          <td className="px-2 py-1"><Checkbox checked={impSel.has(i)} onCheckedChange={() => toggleImpRow(i)} aria-label="Select row" data-testid={`tc-import-select-${i}`} /></td>
                          <td className="px-2 py-1"><Input value={r.company} onChange={(e) => editParsed(i, "company", e.target.value)} className="h-7 text-xs" /></td>
                          <td className="min-w-[150px] px-2 py-1"><IndustrySelect value={r.industry || ""} onChange={(v) => editParsed(i, "industry", v)} className="h-7 text-xs" testid={`tc-import-industry-${i}`} /></td>
                          <td className="px-2 py-1"><Input value={r.region || ""} onChange={(e) => editParsed(i, "region", e.target.value)} className="h-7 text-xs" /></td>
                          <td className="min-w-[200px] px-2 py-1"><OemSelect options={brands} value={r.oem_brands || []} onChange={(v) => editParsed(i, "oem_brands", v)} testid={`tc-import-oem-${i}`} onCreatePartner={createPartner} /></td>
                          <td className="px-2 py-1"><span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${stMeta(r.status || "not_contacted").cls}`}>{stMeta(r.status || "not_contacted").label}</span></td>
                          <td className="px-2 py-1 text-right"><button onClick={() => dropParsed(i)} className="text-slate-400 hover:text-destructive"><X className="h-3.5 w-3.5" /></button></td>
                        </tr>
                      ))}
                      {!imp.parsed.length && <tr><td colSpan={7} className="px-2 py-6 text-center text-muted-foreground">No rows — try another file.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeImport}>Cancel</Button>
            <Button onClick={doImport} disabled={!imp?.parsed?.length || imp?.busy} data-testid="tc-import-save">{imp?.busy && imp?.parsed ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Import {imp?.parsed?.length ? `${imp.parsed.length}` : ""}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
